/**
 * Classificação local (sem IA, sem Maps, sem créditos) de termos extraídos do
 * pedido do utilizador: distingue uma CATEGORIA genérica ("hotéis",
 * "restaurantes", "um café") de um LOCAL específico ("Hotel Milano Scala",
 * "Café Majestic", "Museu do Louvre").
 *
 * Só locais específicos podem entrar em `requiredPlaces`; categorias viram
 * preferências e nunca produzem o erro "Não foi possível validar e incluir".
 */

export type PlaceTermCategory =
  | "accommodation"
  | "restaurant"
  | "cafe"
  | "bakery"
  | "icecream"
  | "bar"
  | "museum"
  | "monument"
  | "church"
  | "park"
  | "beach"
  | "viewpoint"
  | "shopping"
  | "market";

export type PlaceTermClassification =
  | { kind: "generic_category"; category: PlaceTermCategory }
  | { kind: "specific_place" };

const norm = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Palavras que, sozinhas ou combinadas, representam apenas uma categoria. */
const CATEGORY_WORDS: Array<[PlaceTermCategory, string[]]> = [
  ["accommodation", [
    "hotel", "hoteis", "hotels", "hotelzimmer", "hostel", "hostels", "hospedaria",
    "pousada", "pousadas", "albergue", "albergues", "alojamento", "alojamentos",
    "acomodacao", "acomodacoes", "accommodation", "accommodations", "lodging",
    "guesthouse", "guest", "house", "bnb", "airbnb", "hebergement", "unterkunft",
    "unterkuenfte", "alberghi", "albergo", "hoteles", "dormir", "pernoitar",
    "sleep", "stay", "schlafen", "dormire", "hotelaria", "resort", "resorts",
  ]],
  ["restaurant", [
    "restaurante", "restaurantes", "restaurant", "restaurants", "ristorante",
    "ristoranti", "restaurante", "comer", "eat", "jantar", "almocar", "almoco",
    "manger", "essen", "gastronomia", "tasca", "tascas", "marisqueira",
  ]],
  ["cafe", ["cafe", "cafes", "cafeteria", "coffee", "shop", "shops", "kaffee", "caffe", "caffetteria", "salon", "the"]],
  ["bakery", ["pastelaria", "pastelarias", "confeitaria", "confeitarias", "padaria", "padarias", "bakery", "bakeries", "pastry", "boulangerie", "panaderia", "backerei"]],
  ["icecream", ["gelataria", "gelatarias", "geladaria", "sorveteria", "gelateria", "ice", "cream", "eisdiele", "heladeria"]],
  ["bar", ["bar", "bares", "bars", "pub", "pubs", "discoteca", "discotecas", "nightlife", "kneipe"]],
  ["museum", ["museu", "museus", "museum", "museums", "museo", "museos", "musee", "musees", "museen", "galeria", "galerias", "gallery"]],
  ["monument", ["monumento", "monumentos", "monument", "monuments", "denkmal", "castelo", "castelos", "castle", "castles"]],
  ["church", ["igreja", "igrejas", "church", "churches", "catedral", "catedrais", "cathedral", "capela", "capelas", "chiesa", "iglesia", "eglise", "kirche"]],
  ["park", ["parque", "parques", "park", "parks", "parc", "parcs", "jardim", "jardins", "garden", "gardens", "jardin", "giardino"]],
  ["beach", ["praia", "praias", "beach", "beaches", "playa", "playas", "plage", "plages", "spiaggia", "strand"]],
  ["viewpoint", ["miradouro", "miradouros", "mirador", "miradores", "viewpoint", "viewpoints", "belvedere", "aussichtspunkt", "vista", "vistas", "panoramico", "panoramica"]],
  ["shopping", ["shopping", "centro", "comercial", "comerciais", "mall", "malls", "loja", "lojas", "shop", "store", "stores", "einkaufszentrum"]],
  ["market", ["mercado", "mercados", "market", "markets", "marche", "mercato", "feira", "feiras"]],
];

const CATEGORY_BY_WORD = new Map<string, PlaceTermCategory>();
for (const [category, words] of CATEGORY_WORDS) {
  for (const word of words) if (!CATEGORY_BY_WORD.has(word)) CATEGORY_BY_WORD.set(word, category);
}

/** Frases inteiras que nunca são um local concreto. */
const GENERIC_PHRASES: Array<[RegExp, PlaceTermCategory]> = [
  [/^(?:um |uma |o |a )?(?:sitio|local|lugar|espaco|onde|donde|ou|where|somewhere|place|posto)?\s*(?:para|pra|to|pour|por|zum|di)?\s*(?:dormir|ficar|pernoitar|descansar|sleep|stay|rest|schlafen|dormire|reposer)\b/, "accommodation"],
  [/^(?:onde|donde|ou|wo|dove)\s+(?:dormir|ficar|me\s+alojar|stay|sleep|schlafen|dormire)\b/, "accommodation"],
  [/^(?:where\s+to\s+(?:stay|sleep))$/, "accommodation"],
  [/^(?:onde|where|donde|ou|dove|wo)\s+(?:comer|eat|comer\s+bem|manger|mangiare|essen)\b/, "restaurant"],
  // Intenção expressa por verbo: "quero dormir", "preciso de descansar",
  // "I want to sleep". Nunca é o nome de um local concreto.
  [
    /^(?:eu\s+)?(?:quero|queria|gostava\s+de|gostaria\s+de|preciso(?:\s+de)?|precisava(?:\s+de)?|tenho\s+de|posso|i\s+(?:want|need)(?:\s+to)?|we\s+(?:want|need)(?:\s+to)?|quiero|necesito|je\s+veux|j\W?ai\s+besoin\s+de|voglio|ho\s+bisogno\s+di|ich\s+(?:will|m[oö]chte)|wir\s+(?:wollen|m[oö]chten))\s+(?:um\s+|uma\s+|de\s+|a\s+|to\s+|zu\s+)?(?:dormir\w*|descans\w*|repous\w*|pernoit\w*|sleep\w*|rest|nap|dormire|riposare|schlafen|ausruhen|reposer)\b/,
    "accommodation",
  ],
  [/^(?:dormir\w*|descans\w*|repous\w*|pernoit\w*|sleep|rest|nap|dormire|riposare|schlafen)(?:\s+(?:um\s+)?(?:pouco|bocado|bit|while|po?')?)?$/, "accommodation"],
];

/** Qualificadores que descrevem, mas não identificam, um estabelecimento. */
const QUALIFIER_PATTERNS: RegExp[] = [
  /\b(?:muito|mais|bem|bastante)\b/g,
  /\b(?:barat[oa]s?|economic[oa]s?|cheap|budget|low\s+cost|acessive(?:l|is)|preco\s+baixo)\b/g,
  /\b(?:luxo|luxuos[oa]s?|luxury|lujo|luxe|boutique|charmoso|charmosa|design)\b/g,
  /\b(?:bom|boa|bons|boas|melhor|melhores|best|good|great|top|bem\s+avaliad[oa]s?|recomendad[oa]s?|tipic[oa]s?|autentic[oa]s?|local|locais|tradicional|tradicionais)\b/g,
  /\b(?:no|na|em|de|do|da|dos|das|in|im|au|al)?\s*centro(?:\s+historico)?\b/g,
  /\b(?:central|centrais|downtown|centro\s+da\s+cidade|city\s+centre|city\s+center|zentral)\b/g,
  /\b(?:familia|familiar|familiares|family|friendly|criancas|kids)\b/g,
  /\b(?:com|com\s+a|with)?\s*(?:piscina|pool|spa|estacionamento|parking|garagem|pequeno\s+almoco|breakfast|wifi|vista|terraco)\b/g,
  /\b(?:perto|proxim[oa]s?|junto|near|close|nahe|cerca)\s+(?:d[aeo]s?\s+|to\s+|of\s+|the\s+)*(?:praia|mar|centro|rota|percurso|beach|sea|route|station|estacao)\b/g,
  /\b(?:romantic[oa]s?|tranquil[oa]s?|calm[oa]s?|moderno?s?|simples?)\b/g,
  /\b(?:para|pra|for|pour)\s+(?:dormir|ficar|descansar|1|2|3|\d+)\s*(?:noites?|nights?|noche?s?)?\b/g,
  /\b\d+\s*(?:estrelas?|stars?|noites?|nights?)\b/g,
];

/** Palavras vazias que não tornam um termo específico. */
const STOPWORDS = new Set([
  "de", "do", "da", "dos", "das", "d", "the", "of", "a", "o", "os", "as", "um", "uma",
  "uns", "umas", "an", "el", "la", "los", "las", "les", "le", "il", "lo", "un", "une",
  "ein", "eine", "der", "die", "das", "e", "y", "and", "et", "und", "com", "with",
  "em", "no", "na", "nos", "nas", "in", "at", "para", "pra", "por", "pour", "algum",
  "alguma", "alguns", "algumas", "qualquer", "quaisquer", "some", "any", "onde",
  "sitio", "sitios", "lugar", "lugares", "local", "locais", "espaco", "ou", "wo", "dove",
]);

export function classifyPlaceTerm(rawTerm: string): PlaceTermClassification {
  const base = norm(rawTerm);
  if (!base) return { kind: "generic_category", category: "accommodation" };

  for (const [pattern, category] of GENERIC_PHRASES) {
    if (pattern.test(base)) return { kind: "generic_category", category };
  }

  let stripped = ` ${base} `;
  for (const pattern of QUALIFIER_PATTERNS) stripped = stripped.replace(pattern, " ");
  const tokens = stripped.split(/\s+/).filter((token) => token && !STOPWORDS.has(token));

  if (!tokens.length) {
    const firstCategoryWord = base.split(" ").find((token) => CATEGORY_BY_WORD.has(token));
    return {
      kind: "generic_category",
      category: firstCategoryWord ? CATEGORY_BY_WORD.get(firstCategoryWord)! : "accommodation",
    };
  }

  const allCategoryWords = tokens.every((token) => CATEGORY_BY_WORD.has(token));
  if (allCategoryWords) {
    return { kind: "generic_category", category: CATEGORY_BY_WORD.get(tokens[0])! };
  }

  return { kind: "specific_place" };
}

export function isGenericPlaceTerm(term: string): boolean {
  return classifyPlaceTerm(term).kind === "generic_category";
}

export type ClassifiedPlaceTerms = {
  /** Locais concretos que podem ser validados no Google Places. */
  requiredPlaces: string[];
  /** Categorias pedidas (preferências) — nunca obrigatórias. */
  categories: PlaceTermCategory[];
  /** True quando o utilizador pediu alojamento sem nomear um hotel. */
  wantsAccommodation: boolean;
};

export function classifyPlaceTerms(terms: readonly string[]): ClassifiedPlaceTerms {
  const requiredPlaces: string[] = [];
  const categories: PlaceTermCategory[] = [];
  for (const term of terms) {
    const value = term.trim();
    if (!value) continue;
    const classification = classifyPlaceTerm(value);
    if (classification.kind === "specific_place") {
      requiredPlaces.push(value);
    } else if (!categories.includes(classification.category)) {
      categories.push(classification.category);
    }
  }
  return {
    requiredPlaces,
    categories,
    wantsAccommodation: categories.includes("accommodation"),
  };
}

/** Mantém apenas locais concretos numa lista de `requiredPlaces`. */
export function filterSpecificPlaces(terms: readonly string[]): string[] {
  return classifyPlaceTerms(terms).requiredPlaces;
}

/**
 * Intenção de dormir/descansar. NÃO pode depender da expressão exacta
 * "sítio/local para dormir": "quero dormir", "preciso de dormir", "pernoitar",
 * "descansar um pouco" e equivalentes EN/ES/FR/IT/DE valem o mesmo.
 */
const ACCOMMODATION_PROMPT =
  /\b(hotel|hoteis|hot[eé]is|hotels|hostel|pousada|alojamento|aloja[cç][aã]o|acomoda[cç][aã]o|accommodation|lodging|unterkunft|h[ée]bergement|albergo|alberghi|dormida|pernoita\w*|pernoct\w*|dormir\w*|dormire|durmo|adormecer|repousar|descans\w*|relaxar|sleep\w*|nap|overnight|stay\s+(?:the\s+)?night|where\s+to\s+stay|rest\b|resting|reposer|repos\b|riposare|riposo|ausruhen|aussschlafen|schlafen|übernachten|ubernachten|passar\s+a\s+noite|pasar\s+la\s+noche|passer\s+la\s+nuit|onde\s+ficar|onde\s+dormir|d[oó]nde\s+dormir|o[uù]\s+dormir|dove\s+dormire|wo\s+schlafen)\b/i;
const OVERNIGHT_HINT = /\b(noite|noites|pernoitar|dormir|overnight|night|nights|nuit|nuits|notte|nacht|hotel|hostel|alojamento|accommodation|stay)\b/i;
const REST_HINT = /\b(descansar|descanso|repousar|rest|resting|reposer|riposare|ausruhen|relaxar|pausa|break)\b/i;

export type AccommodationRequest = {
  wanted: boolean;
  /** Precisa de esclarecimento: dormir a noite vs. só descansar umas horas. */
  ambiguous: boolean;
  /** Preferências livres detetadas (ex.: "centro", "barato"). */
  preferences: string[];
};

const PREFERENCE_HINTS: Array<[RegExp, string]> = [
  [/\b(barat|economic|cheap|budget)\w*/i, "budget"],
  [/\b(luxo|luxury|lujo|luxe|boutique)\w*/i, "luxury"],
  [/\b(centro|central|downtown|city\s+cent)\w*/i, "central"],
  [/\b(praia|beach|playa|plage|mar)\w*/i, "beach"],
  [/\b(piscina|pool)\w*/i, "pool"],
  [/\b(familia|family|criancas|crianças|kids)\w*/i, "family"],
  [/\b(estacionamento|parking|garagem)\w*/i, "parking"],
];

export function detectAccommodationRequest(
  prompt: string,
  durationHours?: number,
): AccommodationRequest {
  const wanted = ACCOMMODATION_PROMPT.test(prompt);
  const preferences = PREFERENCE_HINTS.filter(([re]) => re.test(prompt)).map(([, key]) => key);
  const shortTrip = typeof durationHours === "number" && durationHours > 0 && durationHours < 24;
  const explicitOvernight = /\b(noite|noites|pernoitar|overnight|night|nights|nuit|notte|nacht|amanh[aã]|tomorrow)\b/i.test(prompt);
  const ambiguous =
    wanted && shortTrip && !explicitOvernight && (REST_HINT.test(prompt) || !OVERNIGHT_HINT.test(prompt) || /dormir|sleep|stay/i.test(prompt));
  return { wanted, ambiguous, preferences };
}

type Lang = string;

export function accommodationClarificationCopy(lang: Lang) {
  const pt = {
    question: "Queres dormir aí esta noite ou só um sítio para descansar durante o dia?",
    overnight: "Dormir (hotel)",
    rest: "Só descansar umas horas",
    note: "Ainda não usámos créditos — isto é só para acertar o plano.",
  };
  const map: Record<string, typeof pt> = {
    pt,
    en: {
      question: "Do you want to sleep there tonight, or just a place to rest during the day?",
      overnight: "Sleep (hotel)",
      rest: "Just rest for a few hours",
      note: "No credits used yet — this only tunes the plan.",
    },
    es: {
      question: "¿Quieres dormir allí esta noche o solo un sitio para descansar durante el día?",
      overnight: "Dormir (hotel)",
      rest: "Solo descansar unas horas",
      note: "Aún no usamos créditos — esto solo ajusta el plan.",
    },
    fr: {
      question: "Tu veux y dormir ce soir, ou juste un endroit pour te reposer dans la journée ?",
      overnight: "Dormir (hôtel)",
      rest: "Juste me reposer quelques heures",
      note: "Aucun crédit utilisé — c'est seulement pour ajuster le plan.",
    },
    de: {
      question: "Möchtest du dort übernachten oder nur tagsüber ausruhen?",
      overnight: "Übernachten (Hotel)",
      rest: "Nur ein paar Stunden ausruhen",
      note: "Noch kein Guthaben verbraucht — das dient nur der Planung.",
    },
    ja: {
      question: "今夜そこに泊まりますか、それとも日中に休む場所ですか？",
      overnight: "宿泊（ホテル）",
      rest: "数時間休むだけ",
      note: "クレジットはまだ使っていません。計画の調整だけです。",
    },
  };
  return map[lang] ?? map.en;
}

/** Mensagem suave quando não há alojamento adequado — a rota mantém-se válida. */
export function accommodationUnavailableCopy(lang: Lang, city: string) {
  const map: Record<string, string> = {
    pt: `Não encontrámos alojamento adequado em ${city}, mas o itinerário está completo.`,
    en: `We couldn't find suitable accommodation in ${city}, but your itinerary is complete.`,
    es: `No encontramos alojamiento adecuado en ${city}, pero el itinerario está completo.`,
    fr: `Nous n'avons pas trouvé d'hébergement adapté à ${city}, mais l'itinéraire est complet.`,
    de: `Wir haben keine passende Unterkunft in ${city} gefunden, dein Plan ist aber vollständig.`,
    ja: `${city}で適した宿泊先は見つかりませんでしたが、旅程は完成しています。`,
  };
  return map[lang] ?? map.en;
}

export function accommodationSectionTitle(lang: Lang) {
  const map: Record<string, string> = {
    pt: "Onde ficar",
    en: "Where to stay",
    es: "Dónde alojarse",
    fr: "Où dormir",
    de: "Wo übernachten",
    ja: "宿泊先",
  };
  return map[lang] ?? map.en;
}
